import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Upload, Save, Trash2, Plus, Edit, Download, Sparkles, Copy } from "lucide-react";
import { api, fileUrl } from "../lib/api";
import { useAuth } from "../contexts/AuthContext";
import { PageHero } from "../components/ui-parts";
import { ODISHA_DISTRICTS } from "../lib/districts";

const TABS = ["settings", "volunteers", "courses", "books", "programmes", "trainings", "gallery", "team", "enrollments", "programme-regs", "orders", "donations", "contact", "ai-assistant"];

async function downloadCsv(endpoint, filename) {
  try {
    const resp = await api.get(endpoint, { responseType: "blob" });
    const blob = new Blob([resp.data], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url; a.download = filename; a.click();
    URL.revokeObjectURL(url);
  } catch (e) {
    toast.error("Download failed");
  }
}

export default function AdminDashboard() {
  const { user, loading } = useAuth();
  const nav = useNavigate();
  const [tab, setTab] = useState("settings");

  useEffect(() => {
    if (loading) return;
    if (!user) nav("/admin/login");
    else if (!user.is_admin) { toast.error("Admin access required"); nav("/"); }
  }, [user, loading, nav]);

  if (loading || !user) return <div className="section container-narrow">Loading…</div>;

  return (
    <div data-testid="admin-page">
      <PageHero badge="Admin" title="Dashboard" subtitle={`Signed in as ${user.name}`} />
      <section className="section">
        <div className="container-narrow">
          <div className="flex flex-wrap gap-2 mb-6">
            {TABS.map(t => <button key={t} onClick={() => setTab(t)} data-testid={`admin-tab-${t}`} className={`px-3 py-1.5 rounded-full text-xs font-semibold uppercase tracking-wide ${tab === t ? "bg-[var(--js-primary)] text-white" : "bg-white border border-[var(--js-border)]"}`}>{t}</button>)}
          </div>
          {tab === "settings" && <SettingsTab />}
          {tab === "volunteers" && <VolunteersTab />}
          {tab === "courses" && <CoursesTab />}
          {tab === "books" && <BooksTab />}
          {tab === "programmes" && <ProgrammesTab />}
          {tab === "trainings" && <TrainingsTab />}
          {tab === "gallery" && <GalleryTab />}
          {tab === "team" && <TeamTab />}
          {tab === "enrollments" && <EnrollmentsTab />}
          {tab === "programme-regs" && <ProgrammeRegsTab />}
          {tab === "orders" && <ListTab endpoint="/admin/book-orders" columns={["ref", "full_name", "total", "district", "mobile"]} title="Book Orders" />}
          {tab === "donations" && <ListTab endpoint="/admin/donations" columns={["ref", "full_name", "amount", "purpose", "mobile"]} title="Donations" />}
          {tab === "contact" && <ListTab endpoint="/admin/contact" columns={["name", "email", "subject", "message"]} title="Contact Messages" />}
          {tab === "ai-assistant" && <AIAssistantTab />}
        </div>
      </section>
    </div>
  );
}

function UploadInput({ value, onChange, label = "Upload image" }) {
  const [busy, setBusy] = useState(false);
  const upload = async (e) => {
    const f = e.target.files?.[0]; if (!f) return;
    setBusy(true);
    try { const fd = new FormData(); fd.append("file", f); const { data } = await api.post("/upload", fd); onChange(data.id); toast.success("Uploaded"); }
    catch { toast.error("Upload failed"); } finally { setBusy(false); }
  };
  return (
    <div>
      <label className="js-btn-outline cursor-pointer !py-1.5 !px-3 !text-xs"><Upload size={12} /> {busy ? "…" : label}<input type="file" className="hidden" onChange={upload} accept="image/*,video/*" /></label>
      {value && <div className="mt-2"><img src={fileUrl(value)} alt="" className="w-24 h-24 object-cover rounded-md border border-[var(--js-border)]" /></div>}
    </div>
  );
}

function SettingsTab() {
  const [s, setS] = useState(null);
  useEffect(() => { api.get("/settings").then(r => setS(r.data)); }, []);
  const save = async () => { await api.patch("/admin/settings", s); toast.success("Saved"); };
  if (!s) return <p>Loading…</p>;
  return (
    <div className="js-card p-6 space-y-4" data-testid="settings-tab">
      <h3 className="font-heading text-xl text-[var(--js-primary)]">Site Settings</h3>
      <div><label className="text-sm font-semibold">Logo</label><UploadInput value={s.logo_url} onChange={v => setS({ ...s, logo_url: v })} label="Upload Logo" /></div>
      <div><label className="text-sm font-semibold">Donation QR Code</label><UploadInput value={s.qr_code_url} onChange={v => setS({ ...s, qr_code_url: v })} label="Upload QR" /></div>
      <div className="grid gap-3 md:grid-cols-2">
        <input placeholder="Organisation Name" value={s.organisation_name || ""} onChange={e => setS({ ...s, organisation_name: e.target.value })} className="px-3 py-2 border border-[var(--js-border)] rounded-md text-sm" />
        <input placeholder="Tagline" value={s.tagline || ""} onChange={e => setS({ ...s, tagline: e.target.value })} className="px-3 py-2 border border-[var(--js-border)] rounded-md text-sm" />
        <input placeholder="Contact address" value={s.contact?.address || ""} onChange={e => setS({ ...s, contact: { ...s.contact, address: e.target.value } })} className="px-3 py-2 border border-[var(--js-border)] rounded-md text-sm" />
        <input placeholder="Contact phone" value={s.contact?.phone || ""} onChange={e => setS({ ...s, contact: { ...s.contact, phone: e.target.value } })} className="px-3 py-2 border border-[var(--js-border)] rounded-md text-sm" />
        <input placeholder="Contact email" value={s.contact?.email || ""} onChange={e => setS({ ...s, contact: { ...s.contact, email: e.target.value } })} className="px-3 py-2 border border-[var(--js-border)] rounded-md text-sm" />
        <input placeholder="Map embed URL" value={s.contact?.map_embed || ""} onChange={e => setS({ ...s, contact: { ...s.contact, map_embed: e.target.value } })} className="px-3 py-2 border border-[var(--js-border)] rounded-md text-sm" />
      </div>
      <textarea placeholder="About (English)" value={s.about?.en || ""} onChange={e => setS({ ...s, about: { ...s.about, en: e.target.value } })} className="w-full px-3 py-2 border border-[var(--js-border)] rounded-md text-sm h-24" />
      <textarea placeholder="Vision" value={s.vision || ""} onChange={e => setS({ ...s, vision: e.target.value })} className="w-full px-3 py-2 border border-[var(--js-border)] rounded-md text-sm h-20" />
      <textarea placeholder="Mission" value={s.mission || ""} onChange={e => setS({ ...s, mission: e.target.value })} className="w-full px-3 py-2 border border-[var(--js-border)] rounded-md text-sm h-20" />
      <button onClick={save} className="js-btn-primary" data-testid="settings-save"><Save size={14} /> Save</button>
    </div>
  );
}

function VolunteersTab() {
  const [list, setList] = useState([]);
  const load = () => api.get("/admin/volunteers").then(r => setList(r.data));
  useEffect(() => { load(); }, []);
  const toggle = async (v, field) => { await api.patch(`/admin/volunteers/${v.id}`, { [field]: !v[field] }); load(); };
  const replacePhoto = async (v, file) => {
    if (!file) return;
    const fd = new FormData(); fd.append("file", file);
    try {
      const { data } = await api.post("/upload", fd);
      await api.patch(`/admin/volunteers/${v.id}`, { photo_url: data.id, photo_approved: true });
      toast.success("Photo replaced and approved"); load();
    } catch { toast.error("Photo replace failed"); }
  };
  const del = async (id) => { if (!window.confirm("Delete?")) return; await api.delete(`/admin/volunteers/${id}`); load(); };
  return (
    <div className="js-card p-6" data-testid="volunteers-tab">
      <h3 className="font-heading text-xl text-[var(--js-primary)] mb-4">Volunteers ({list.length})</h3>
      <div className="overflow-auto">
        <table className="w-full text-sm">
          <thead className="text-left border-b border-[var(--js-border)]"><tr>
            <th className="p-2 uppercase text-xs">Photo</th>
            <th className="p-2 uppercase text-xs">Name</th>
            <th className="p-2 uppercase text-xs">District</th>
            <th className="p-2 uppercase text-xs">Approved</th>
            <th className="p-2 uppercase text-xs">Coordinator</th>
            <th className="p-2 uppercase text-xs">Photo Status</th>
            <th></th>
          </tr></thead>
          <tbody>
            {list.map(v => (
              <tr key={v.id} className="border-b border-[var(--js-border)]" data-testid={`vol-row-${v.id}`}>
                <td className="p-2">
                  {v.photo_url ? (
                    <img src={fileUrl(v.photo_url)} alt="" className={`w-12 h-12 rounded-full object-cover border-2 ${v.photo_approved ? "border-[var(--js-accent)]" : "border-orange-400 opacity-70"}`} />
                  ) : (
                    <div className="w-12 h-12 rounded-full bg-[var(--js-bg)] border border-[var(--js-border)] flex items-center justify-center text-xs text-[var(--js-muted)]">—</div>
                  )}
                </td>
                <td className="p-2">{v.full_name}<div className="text-xs text-[var(--js-muted)]">{v.email}</div></td>
                <td className="p-2">{v.district}</td>
                <td className="p-2"><button onClick={() => toggle(v, "approved")} className={`px-2 py-1 rounded text-xs ${v.approved ? "bg-[var(--js-accent)] text-white" : "bg-[var(--js-border)]"}`} data-testid={`approve-${v.id}`}>{v.approved ? "Yes" : "No"}</button></td>
                <td className="p-2"><button onClick={() => toggle(v, "is_coordinator")} className={`px-2 py-1 rounded text-xs ${v.is_coordinator ? "bg-[var(--js-primary)] text-white" : "bg-[var(--js-border)]"}`}>{v.is_coordinator ? "Yes" : "No"}</button></td>
                <td className="p-2">
                  {v.photo_url ? (
                    <div className="flex flex-wrap gap-1 items-center">
                      <button onClick={() => toggle(v, "photo_approved")} data-testid={`photo-approve-${v.id}`}
                        className={`px-2 py-1 rounded text-xs ${v.photo_approved ? "bg-[var(--js-accent)] text-white" : "bg-orange-100 text-orange-800 border border-orange-300"}`}>
                        {v.photo_approved ? "Approved" : "Pending review"}
                      </button>
                      <label className="text-xs cursor-pointer underline text-[var(--js-primary)]" data-testid={`photo-replace-${v.id}`}>
                        Replace
                        <input type="file" accept="image/*" className="hidden" onChange={e => replacePhoto(v, e.target.files?.[0])} />
                      </label>
                    </div>
                  ) : (
                    <span className="text-xs text-[var(--js-muted)]">No photo</span>
                  )}
                </td>
                <td className="p-2"><button onClick={() => del(v.id)} className="text-[var(--js-primary)]" data-testid={`vol-del-${v.id}`}><Trash2 size={14} /></button></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <p className="text-xs text-[var(--js-muted)] mt-4">Volunteer photos are hidden from the public directory until approved here.</p>
    </div>
  );
}

function CrudTab({ title, endpoint, publicEndpoint, fields, testid }) {
  const [list, setList] = useState([]);
  const [editing, setEditing] = useState(null);
  const load = () => api.get(publicEndpoint).then(r => setList(r.data));
  useEffect(() => { load(); }, []);
  const save = async () => {
    if (editing.id) await api.patch(`${endpoint}/${editing.id}`, editing);
    else await api.post(endpoint, editing);
    setEditing(null); load(); toast.success("Saved");
  };
  const del = async (id) => { if (!window.confirm("Delete?")) return; await api.delete(`${endpoint}/${id}`); load(); };
  return (
    <div className="js-card p-6" data-testid={testid}>
      <div className="flex justify-between items-center mb-4">
        <h3 className="font-heading text-xl text-[var(--js-primary)]">{title} ({list.length})</h3>
        <button onClick={() => setEditing({})} className="js-btn-primary !py-1.5 !px-3 !text-xs" data-testid={`${testid}-add`}><Plus size={12} /> Add</button>
      </div>
      <div className="space-y-2">
        {list.map(item => (
          <div key={item.id} className="flex items-center justify-between p-3 border border-[var(--js-border)] rounded-md">
            <div className="text-sm"><b>{item.name || item.title}</b>{item.district && <span className="text-[var(--js-muted)]"> · {item.district}</span>}</div>
            <div className="flex gap-2"><button onClick={() => setEditing(item)}><Edit size={14} /></button><button onClick={() => del(item.id)} className="text-[var(--js-primary)]"><Trash2 size={14} /></button></div>
          </div>
        ))}
      </div>
      {editing && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={() => setEditing(null)}>
          <div className="js-card p-6 max-w-lg w-full max-h-[90vh] overflow-auto" onClick={e => e.stopPropagation()}>
            <h4 className="font-heading text-lg mb-4">{editing.id ? "Edit" : "Add"}</h4>
            <div className="space-y-3">
              {fields.map(f => {
                if (f.type === "textarea") return <textarea key={f.k} placeholder={f.l} value={editing[f.k] || ""} onChange={e => setEditing({ ...editing, [f.k]: e.target.value })} className="w-full px-3 py-2 border border-[var(--js-border)] rounded-md text-sm h-20" />;
                if (f.type === "district") return <select key={f.k} value={editing[f.k] || ""} onChange={e => setEditing({ ...editing, [f.k]: e.target.value })} className="w-full px-3 py-2 border border-[var(--js-border)] rounded-md text-sm"><option value="">{f.l}</option>{ODISHA_DISTRICTS.map(d => <option key={d}>{d}</option>)}</select>;
                if (f.type === "select") return <select key={f.k} value={editing[f.k] || ""} onChange={e => setEditing({ ...editing, [f.k]: e.target.value })} className="w-full px-3 py-2 border border-[var(--js-border)] rounded-md text-sm"><option value="">{f.l}</option>{f.opts.map(o => <option key={o}>{o}</option>)}</select>;
                if (f.type === "bool") return <label key={f.k} className="flex items-center gap-2 text-sm"><input type="checkbox" checked={!!editing[f.k]} onChange={e => setEditing({ ...editing, [f.k]: e.target.checked })} /> {f.l}</label>;
                if (f.type === "upload") return <div key={f.k}><label className="text-sm font-semibold block mb-1">{f.l}</label><UploadInput value={editing[f.k]} onChange={v => setEditing({ ...editing, [f.k]: v })} /></div>;
                return <input key={f.k} type={f.type || "text"} placeholder={f.l} value={editing[f.k] || ""} onChange={e => setEditing({ ...editing, [f.k]: f.type === "number" ? parseFloat(e.target.value) || 0 : e.target.value })} className="w-full px-3 py-2 border border-[var(--js-border)] rounded-md text-sm" />;
              })}
            </div>
            <div className="flex gap-2 mt-4"><button onClick={save} className="js-btn-primary"><Save size={14} /> Save</button><button onClick={() => setEditing(null)} className="js-btn-outline">Cancel</button></div>
          </div>
        </div>
      )}
    </div>
  );
}

function CoursesTab() {
  return <CrudTab title="Courses" endpoint="/admin/courses" publicEndpoint="/courses" testid="courses-tab" fields={[
    { k: "name", l: "Course name" }, { k: "description", l: "Description", type: "textarea" },
    { k: "level", l: "Level", type: "select", opts: ["Beginner", "Intermediate", "Advanced"] },
    { k: "duration", l: "Duration" }, { k: "mode", l: "Mode", type: "select", opts: ["Online", "Offline", "Hybrid"] },
    { k: "language", l: "Language" }, { k: "eligibility", l: "Eligibility" },
    { k: "start_date", l: "Start date" }, { k: "fee", l: "Fee (₹)", type: "number" },
    { k: "is_free", l: "Is Free?", type: "bool" }, { k: "district", l: "District", type: "district" },
    { k: "instructor", l: "Instructor" }, { k: "syllabus", l: "Syllabus", type: "textarea" },
    { k: "image_url", l: "Image", type: "upload" },
  ]} />;
}
function BooksTab() {
  return <CrudTab title="Books" endpoint="/admin/books" publicEndpoint="/books" testid="books-tab" fields={[
    { k: "title", l: "Title" }, { k: "author", l: "Author" },
    { k: "description", l: "Description", type: "textarea" }, { k: "category", l: "Category" },
    { k: "language", l: "Language" }, { k: "price", l: "Price (₹)", type: "number" },
    { k: "availability", l: "Availability" }, { k: "cover_url", l: "Cover", type: "upload" },
  ]} />;
}
function ProgrammesTab() {
  return <CrudTab title="Programmes" endpoint="/admin/programmes" publicEndpoint="/programmes" testid="programmes-tab" fields={[
    { k: "name", l: "Name" }, { k: "date", l: "Date" }, { k: "time", l: "Time" },
    { k: "venue", l: "Venue" }, { k: "district", l: "District", type: "district" },
    { k: "organiser", l: "Organiser" }, { k: "description", l: "Description", type: "textarea" },
    { k: "status", l: "Status", type: "select", opts: ["upcoming", "ongoing", "completed"] },
    { k: "poster_url", l: "Poster", type: "upload" },
  ]} />;
}
function TrainingsTab() {
  return <CrudTab title="Trainings" endpoint="/admin/trainings" publicEndpoint="/trainings" testid="trainings-tab" fields={[
    { k: "title", l: "Title" }, { k: "description", l: "Description", type: "textarea" },
    { k: "trainer", l: "Trainer" }, { k: "date", l: "Date" }, { k: "duration", l: "Duration" },
    { k: "location", l: "Location" }, { k: "mode", l: "Mode", type: "select", opts: ["Online", "Offline", "Hybrid"] },
    { k: "eligibility", l: "Eligibility" }, { k: "district", l: "District", type: "district" },
    { k: "certificate", l: "Certificate provided", type: "bool" },
  ]} />;
}
function GalleryTab() {
  return <CrudTab title="Gallery" endpoint="/admin/gallery" publicEndpoint="/gallery" testid="gallery-tab" fields={[
    { k: "title", l: "Title" }, { k: "category", l: "Category", type: "select", opts: ["Sanskrit Programmes", "Spoken Sanskrit Classes", "Training", "Volunteer Activities", "District Activities", "Camps", "Workshops", "Cultural"] },
    { k: "district", l: "District", type: "district" }, { k: "media_type", l: "Type", type: "select", opts: ["image", "video"] },
    { k: "file_url", l: "Media", type: "upload" }, { k: "description", l: "Description", type: "textarea" },
  ]} />;
}
function NewsTab_REMOVED() { return null; }

function AIAssistantTab() {
  const [kind, setKind] = useState("course");
  const [prompt, setPrompt] = useState("");
  const [output, setOutput] = useState("");
  const [busy, setBusy] = useState(false);
  const generate = async (e) => {
    e.preventDefault();
    if (!prompt.trim()) { toast.error("Please enter a prompt"); return; }
    setBusy(true); setOutput("");
    try {
      const { data } = await api.post("/admin/ai/generate", { kind, prompt });
      setOutput(data.text || "");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Generation failed");
    } finally { setBusy(false); }
  };
  const copy = async () => { try { await navigator.clipboard.writeText(output); toast.success("Copied to clipboard"); } catch {} };
  return (
    <div className="js-card p-6" data-testid="ai-assistant-tab">
      <div className="flex items-center gap-2 mb-4">
        <Sparkles size={18} className="text-[var(--js-primary)]" />
        <h3 className="font-heading text-xl text-[var(--js-primary)]">AI Content Assistant</h3>
      </div>
      <p className="text-sm text-[var(--js-muted)] mb-4">Describe what you want, pick the content type, and GPT 6 Sol will draft it in Jayatu Sanskrutam's voice. Review before publishing.</p>
      <form onSubmit={generate} className="space-y-3">
        <div className="flex flex-wrap gap-2">
          {[["course","Course description"],["programme","Programme announcement"],["training","Training description"],["news","News / announcement"],["book","Book description"]].map(([k,l]) => (
            <button type="button" key={k} onClick={() => setKind(k)} data-testid={`ai-kind-${k}`}
              className={`px-3 py-1.5 rounded-full text-xs font-semibold ${kind===k ? "bg-[var(--js-primary)] text-white" : "bg-white border border-[var(--js-border)] text-[var(--js-muted)]"}`}>{l}</button>
          ))}
        </div>
        <textarea value={prompt} onChange={e => setPrompt(e.target.value)} data-testid="ai-prompt"
          placeholder="Example: 10-day spoken Sanskrit camp for school teachers in Puri, mornings, free of cost, taught by Dr. XYZ."
          className="w-full px-3 py-2 rounded-md border border-[var(--js-border)] text-sm h-28" />
        <button type="submit" disabled={busy} className="js-btn-primary disabled:opacity-60" data-testid="ai-generate-btn">
          <Sparkles size={14} /> {busy ? "Drafting…" : "Draft with AI"}
        </button>
      </form>
      {output && (
        <div className="mt-6" data-testid="ai-output">
          <div className="flex items-center justify-between mb-2">
            <div className="text-xs uppercase font-semibold text-[var(--js-secondary)]">Draft</div>
            <button onClick={copy} className="js-btn-outline !py-1 !px-2 !text-xs" data-testid="ai-copy-btn"><Copy size={12} /> Copy</button>
          </div>
          <textarea value={output} onChange={e => setOutput(e.target.value)}
            className="w-full px-3 py-2 rounded-md border border-[var(--js-border)] text-sm h-64 font-mono" />
          <p className="text-xs text-[var(--js-muted)] mt-2">Edit above, then copy and paste into the matching form (Courses, Programmes, News, etc.) and save.</p>
        </div>
      )}
    </div>
  );
}


function EnrollmentsTab() {
  const [enrollments, setEnrollments] = useState([]);
  const [courses, setCourses] = useState([]);
  const [courseFilter, setCourseFilter] = useState("");
  const [q, setQ] = useState("");
  const [sort, setSort] = useState("desc");
  const [detail, setDetail] = useState(null);

  useEffect(() => {
    api.get("/admin/enrollments").then(r => setEnrollments(r.data)).catch(() => {});
    api.get("/courses").then(r => setCourses(r.data)).catch(() => {});
  }, []);

  const courseObj = (id) => courses.find(c => c.id === id);
  const courseName = (id) => courseObj(id)?.name || id || "—";

  const countByCourse = enrollments.reduce((a, e) => { a[e.course_id] = (a[e.course_id] || 0) + 1; return a; }, {});

  const filtered = enrollments
    .filter(e => !courseFilter || e.course_id === courseFilter)
    .filter(e => {
      if (!q) return true;
      const ql = q.toLowerCase();
      return (e.full_name || "").toLowerCase().includes(ql)
        || (e.mobile || "").toLowerCase().includes(ql)
        || (e.email || "").toLowerCase().includes(ql)
        || (e.ref || "").toLowerCase().includes(ql);
    })
    .sort((a, b) => {
      const da = new Date(a.created_at || 0).getTime();
      const dbb = new Date(b.created_at || 0).getTime();
      return sort === "desc" ? dbb - da : da - dbb;
    });

  const feeLabel = (e) => {
    const c = courseObj(e.course_id);
    if (!c) return "—";
    if (c.is_free) return "Free";
    return c.fee ? `₹ ${c.fee}` : "Paid";
  };

  return (
    <div className="js-card p-6" data-testid="enrollments-tab">
      <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
        <h3 className="font-heading text-xl text-[var(--js-primary)]">Enrollments ({filtered.length}{filtered.length !== enrollments.length ? ` of ${enrollments.length}` : ""})</h3>
        <div className="flex items-center gap-3">
          <button onClick={() => downloadCsv("/admin/enrollments.csv", "enrollments.csv")} data-testid="enroll-csv-btn" className="js-btn-outline !py-1.5 !px-3 !text-xs">
            <Download size={12} /> Download CSV
          </button>
          <div className="text-xs text-[var(--js-muted)]">Only admins can view this data.</div>
        </div>
      </div>

      {courses.length > 0 && (
        <div className="mb-4 flex flex-wrap gap-2" data-testid="enroll-course-counts">
          {courses.map(c => (
            <button key={c.id} onClick={() => setCourseFilter(courseFilter === c.id ? "" : c.id)} data-testid={`enroll-course-chip-${c.id}`}
              className={`js-chip cursor-pointer ${courseFilter === c.id ? "!bg-[var(--js-primary)] !text-white !border-[var(--js-primary)]" : ""}`}>
              {c.name} · {countByCourse[c.id] || 0}
            </button>
          ))}
        </div>
      )}

      <div className="flex flex-wrap gap-3 mb-4">
        <select value={courseFilter} onChange={e => setCourseFilter(e.target.value)} data-testid="enroll-filter-course"
          className="px-3 py-2 rounded-md border border-[var(--js-border)] text-sm bg-white">
          <option value="">All programs</option>
          {courses.map(c => <option key={c.id} value={c.id}>{c.name} ({countByCourse[c.id] || 0})</option>)}
        </select>
        <input value={q} onChange={e => setQ(e.target.value)} placeholder="Search name / mobile / email / ref" data-testid="enroll-search"
          className="flex-1 min-w-[240px] px-3 py-2 rounded-md border border-[var(--js-border)] text-sm" />
        <button onClick={() => setSort(s => s === "desc" ? "asc" : "desc")} data-testid="enroll-sort-toggle"
          className="px-3 py-2 rounded-md border border-[var(--js-border)] text-sm bg-white hover:border-[var(--js-primary)]">
          Date: {sort === "desc" ? "Newest first" : "Oldest first"}
        </button>
      </div>

      <div className="overflow-auto">
        <table className="w-full text-sm">
          <thead className="text-left border-b border-[var(--js-border)]">
            <tr>
              {["Ref", "Program", "Name", "Mobile", "Email", "District", "Date", "Fee", "Status"].map(h => (
                <th key={h} className="p-2 uppercase text-xs text-[var(--js-muted)]">{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {filtered.length === 0 && <tr><td colSpan={9} className="p-6 text-center text-[var(--js-muted)]">No enrollments match.</td></tr>}
            {filtered.map(e => (
              <tr key={e.id} onClick={() => setDetail(e)} data-testid={`enroll-row-${e.id}`}
                className="border-b border-[var(--js-border)] cursor-pointer hover:bg-[var(--js-bg)] transition-colors">
                <td className="p-2 font-mono text-xs text-[var(--js-primary)]">{e.ref}</td>
                <td className="p-2">{courseName(e.course_id)}</td>
                <td className="p-2 font-semibold">{e.full_name}</td>
                <td className="p-2">{e.mobile}</td>
                <td className="p-2 text-xs">{e.email}</td>
                <td className="p-2 text-xs">{e.district}</td>
                <td className="p-2 text-xs">{e.created_at ? new Date(e.created_at).toLocaleDateString() : "—"}</td>
                <td className="p-2 text-xs">{feeLabel(e)}</td>
                <td className="p-2"><span className="js-chip">{e.status || "pending"}</span></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {detail && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={() => setDetail(null)}>
          <div className="js-card p-6 max-w-lg w-full max-h-[90vh] overflow-auto" onClick={e => e.stopPropagation()} data-testid="enroll-detail-modal">
            <h4 className="font-heading text-xl text-[var(--js-primary)]">Enrollment {detail.ref}</h4>
            <div className="text-xs text-[var(--js-muted)] mt-1">Program: {courseName(detail.course_id)}</div>
            <div className="mt-4 space-y-2 text-sm">
              {Object.entries(detail).filter(([k]) => !["id", "updated_at"].includes(k)).map(([k, v]) => (
                <div key={k} className="grid grid-cols-3 gap-3 border-b border-[var(--js-border)] py-1">
                  <span className="text-[var(--js-muted)] uppercase text-xs">{k.replace(/_/g, " ")}</span>
                  <span className="col-span-2 break-all">{k === "course_id" ? courseName(v) : k === "created_at" && v ? new Date(v).toLocaleString() : String(v ?? "—")}</span>
                </div>
              ))}
            </div>
            <button onClick={() => setDetail(null)} data-testid="enroll-detail-close" className="js-btn-outline mt-4">Close</button>
          </div>
        </div>
      )}
    </div>
  );
}

function ProgrammeRegsTab() {
  const [regs, setRegs] = useState([]);
  const [programmes, setProgrammes] = useState([]);
  const [progFilter, setProgFilter] = useState("");
  const [q, setQ] = useState("");
  const [sort, setSort] = useState("desc");
  const [detail, setDetail] = useState(null);

  useEffect(() => {
    api.get("/admin/programme-registrations").then(r => setRegs(r.data)).catch(() => {});
    api.get("/programmes").then(r => setProgrammes(r.data)).catch(() => {});
  }, []);

  const progName = (id) => programmes.find(p => p.id === id)?.name || id || "—";
  const countByProg = regs.reduce((a, e) => { a[e.programme_id] = (a[e.programme_id] || 0) + 1; return a; }, {});

  const filtered = regs
    .filter(e => !progFilter || e.programme_id === progFilter)
    .filter(e => {
      if (!q) return true;
      const ql = q.toLowerCase();
      return (e.full_name || "").toLowerCase().includes(ql)
        || (e.mobile || "").toLowerCase().includes(ql)
        || (e.email || "").toLowerCase().includes(ql)
        || (e.ref || "").toLowerCase().includes(ql);
    })
    .sort((a, b) => {
      const da = new Date(a.created_at || 0).getTime();
      const dbb = new Date(b.created_at || 0).getTime();
      return sort === "desc" ? dbb - da : da - dbb;
    });

  return (
    <div className="js-card p-6" data-testid="prog-regs-tab">
      <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
        <h3 className="font-heading text-xl text-[var(--js-primary)]">Programme Registrations ({filtered.length}{filtered.length !== regs.length ? ` of ${regs.length}` : ""})</h3>
        <div className="flex items-center gap-3">
          <button onClick={() => downloadCsv("/admin/programme-registrations.csv", "programme-registrations.csv")} data-testid="preg-csv-btn" className="js-btn-outline !py-1.5 !px-3 !text-xs">
            <Download size={12} /> Download CSV
          </button>
          <div className="text-xs text-[var(--js-muted)]">Admin only.</div>
        </div>
      </div>

      <div className="flex flex-wrap gap-3 mb-4">
        <select value={progFilter} onChange={e => setProgFilter(e.target.value)} data-testid="preg-filter"
          className="px-3 py-2 rounded-md border border-[var(--js-border)] text-sm bg-white">
          <option value="">All programmes</option>
          {programmes.map(p => <option key={p.id} value={p.id}>{p.name} ({countByProg[p.id] || 0})</option>)}
        </select>
        <input value={q} onChange={e => setQ(e.target.value)} placeholder="Search name / mobile / email / ref" data-testid="preg-search"
          className="flex-1 min-w-[240px] px-3 py-2 rounded-md border border-[var(--js-border)] text-sm" />
        <button onClick={() => setSort(s => s === "desc" ? "asc" : "desc")} data-testid="preg-sort"
          className="px-3 py-2 rounded-md border border-[var(--js-border)] text-sm bg-white hover:border-[var(--js-primary)]">
          Date: {sort === "desc" ? "Newest first" : "Oldest first"}
        </button>
      </div>

      <div className="overflow-auto">
        <table className="w-full text-sm">
          <thead className="text-left border-b border-[var(--js-border)]">
            <tr>{["Ref", "Programme", "Name", "Mobile", "Email", "District", "Date"].map(h => <th key={h} className="p-2 uppercase text-xs text-[var(--js-muted)]">{h}</th>)}</tr>
          </thead>
          <tbody>
            {filtered.length === 0 && <tr><td colSpan={7} className="p-6 text-center text-[var(--js-muted)]">No registrations match.</td></tr>}
            {filtered.map(e => (
              <tr key={e.id} onClick={() => setDetail(e)} data-testid={`preg-row-${e.id}`} className="border-b border-[var(--js-border)] cursor-pointer hover:bg-[var(--js-bg)]">
                <td className="p-2 font-mono text-xs text-[var(--js-primary)]">{e.ref}</td>
                <td className="p-2">{progName(e.programme_id)}</td>
                <td className="p-2 font-semibold">{e.full_name}</td>
                <td className="p-2">{e.mobile}</td>
                <td className="p-2 text-xs">{e.email}</td>
                <td className="p-2 text-xs">{e.district}</td>
                <td className="p-2 text-xs">{e.created_at ? new Date(e.created_at).toLocaleDateString() : "—"}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {detail && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={() => setDetail(null)}>
          <div className="js-card p-6 max-w-lg w-full max-h-[90vh] overflow-auto" onClick={e => e.stopPropagation()} data-testid="preg-detail-modal">
            <h4 className="font-heading text-xl text-[var(--js-primary)]">Registration {detail.ref}</h4>
            <div className="text-xs text-[var(--js-muted)] mt-1">Programme: {progName(detail.programme_id)}</div>
            <div className="mt-4 space-y-2 text-sm">
              {Object.entries(detail).filter(([k]) => !["id", "updated_at"].includes(k)).map(([k, v]) => (
                <div key={k} className="grid grid-cols-3 gap-3 border-b border-[var(--js-border)] py-1">
                  <span className="text-[var(--js-muted)] uppercase text-xs">{k.replace(/_/g, " ")}</span>
                  <span className="col-span-2 break-all">{k === "programme_id" ? progName(v) : k === "created_at" && v ? new Date(v).toLocaleString() : String(v ?? "—")}</span>
                </div>
              ))}
            </div>
            <button onClick={() => setDetail(null)} className="js-btn-outline mt-4">Close</button>
          </div>
        </div>
      )}
    </div>
  );
}
function TeamTab() {
  return <CrudTab title="Team" endpoint="/admin/team" publicEndpoint="/team" testid="team-tab" fields={[
    { k: "name", l: "Name" }, { k: "designation", l: "Designation" },
    { k: "bio", l: "Short bio", type: "textarea" }, { k: "email", l: "Email" },
    { k: "phone", l: "Phone" }, { k: "photo_url", l: "Photo", type: "upload" },
    { k: "order", l: "Order", type: "number" },
  ]} />;
}
function ListTab({ endpoint, columns, title }) {
  const [list, setList] = useState([]);
  useEffect(() => { api.get(endpoint).then(r => setList(r.data)); }, [endpoint]);
  return (
    <div className="js-card p-6">
      <h3 className="font-heading text-xl text-[var(--js-primary)] mb-4">{title} ({list.length})</h3>
      <div className="overflow-auto"><table className="w-full text-sm">
        <thead className="text-left border-b border-[var(--js-border)]"><tr>{columns.map(c => <th key={c} className="p-2 uppercase text-xs">{c}</th>)}</tr></thead>
        <tbody>{list.map(item => <tr key={item.id} className="border-b border-[var(--js-border)]">{columns.map(c => <td key={c} className="p-2">{String(item[c] ?? "—").slice(0, 80)}</td>)}</tr>)}</tbody>
      </table></div>
    </div>
  );
}
